import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import ForgeShell, { ControlLabel, Segmented, Chips, Textarea, Select, OutputCard, CopyableText, EmptyState } from "./ForgeShell";
import { useForgeModule, GenerateButton, ActionRow, DraftsList } from "./useForgeModule";

const GOALS = [
  { value: "convert", label: "Convert" },
  { value: "scale", label: "Scale" },
  { value: "premium", label: "Premium" },
  { value: "rapid_cash", label: "Rapid cash" },
];

function ScoreBar({ label, value }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className="space-y-1" data-testid={`offer-score-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex justify-between text-[11px]">
        <span className="text-meta font-semibold uppercase tracking-wider">{label}</span>
        <span className="text-ink font-bold">{pct}</span>
      </div>
      <div className="h-1.5 rounded-full bg-zinc-100 overflow-hidden">
        <div className="h-full ashra-gradient-bg" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function OfferLab() {
  const m = useForgeModule("offer");
  const [products, setProducts] = useState([]);
  useEffect(() => { api.get("/products").then(r => setProducts(r.data || [])).catch(() => {}); }, []);
  const i = m.inputs;
  const set = (k, v) => m.setInputs({ ...i, [k]: v });
  const score = m.output?.score || {};

  return (
    <ForgeShell
      testid="forge-offer-lab"
      left={
        <>
          <ControlLabel hint="What you're packaging">Product</ControlLabel>
          <Select testid="offer-product" value={i.product_id || ""} onChange={(v) => {
            const p = products.find(x => x.id === v);
            m.setInputs({ ...i, product_id: v, product_name: p?.name, product_price: p?.price });
          }} placeholder="Pick from catalog" options={products.map(p => ({ value: p.id, label: `${p.name} — $${p.price}` }))} />

          <ControlLabel>Audience</ControlLabel>
          <Textarea testid="offer-audience" value={i.audience} onChange={(v) => set("audience", v)} rows={3}
            placeholder="Solo creators at $5–25k/mo trying to systemize revenue" />

          <ControlLabel>Goal</ControlLabel>
          <Chips testid="offer-goal" options={GOALS} value={i.goal || "convert"} onChange={(v) => set("goal", v)} />

          <ControlLabel hint="Anything specific the offer must include">Notes</ControlLabel>
          <Textarea testid="offer-notes" value={i.notes} onChange={(v) => set("notes", v)} rows={2}
            placeholder="3-pay plan, 14-day refund, founder access" />

          <GenerateButton testid="offer-generate" loading={m.loading} onClick={m.generate} />
        </>
      }
      center={
        !m.output ? (
          <EmptyState title="Build your next offer" body="Headline, pricing, transformation, urgency, bonus stack — plus an offer score on clarity, value, urgency and scalability." />
        ) : (
          <>
            <ActionRow testid="offer-actions" onRegenerate={m.generate} onSave={() => m.saveDraft(m.output.offer_headline)} regenerating={m.loading} />

            <OutputCard title="Offer headline">
              <CopyableText testid="offer-headline" text={m.output.offer_headline} />
            </OutputCard>

            <div className="grid md:grid-cols-2 gap-4">
              <OutputCard title="Transformation">
                <CopyableText testid="offer-transformation" text={m.output.transformation_statement} />
              </OutputCard>
              <OutputCard title="Pricing structure">
                <div className="rounded-xl bg-zinc-50 border border-line p-3 text-sm">
                  <p className="font-display text-2xl font-extrabold text-ink">${m.output.pricing_structure?.main ?? "—"}</p>
                  <p className="text-xs text-meta mt-1">{m.output.pricing_structure?.format || "—"}</p>
                  {m.output.pricing_structure?.installments_hint && (
                    <p className="text-xs text-ai mt-2 font-medium">{m.output.pricing_structure.installments_hint}</p>
                  )}
                </div>
              </OutputCard>
            </div>

            <OutputCard title="Urgency angles">
              <ul className="space-y-2">
                {(m.output.urgency_suggestions || []).map((u, idx) => <li key={idx}><CopyableText testid={`offer-urgency-${idx}`} text={u} /></li>)}
              </ul>
            </OutputCard>

            <OutputCard title="Bonus stack">
              <ul className="divide-y divide-line">
                {(m.output.bonus_stack || []).map((b, idx) => (
                  <li key={idx} className="py-2.5 flex items-center gap-3" data-testid={`offer-bonus-${idx}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-ink">{b.name}</p>
                      <p className="text-xs text-meta">{b.why}</p>
                    </div>
                    <span className="text-success font-bold text-sm shrink-0">${Math.round(b.value_usd || 0).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </OutputCard>

            <OutputCard title="Positioning angles">
              <ul className="space-y-2">
                {(m.output.positioning_angles || []).map((p, idx) => <li key={idx}><CopyableText testid={`offer-position-${idx}`} text={p} /></li>)}
              </ul>
            </OutputCard>
          </>
        )
      }
      right={
        <>
          <div data-testid="offer-score">
            <ControlLabel hint="Live read on this offer">Offer score</ControlLabel>
            <div className="mt-3 space-y-3">
              <ScoreBar label="Clarity"         value={score.clarity} />
              <ScoreBar label="Perceived value" value={score.perceived_value} />
              <ScoreBar label="Urgency"         value={score.urgency} />
              <ScoreBar label="Scalability"     value={score.scalability} />
            </div>
            {!m.output && <p className="text-[11px] text-meta-soft mt-3">Generate an offer to see the score.</p>}
          </div>
          <DraftsList drafts={m.drafts} onLoad={m.loadDraft} onDelete={m.deleteDraft}
            onToggleFavorite={m.toggleFavorite} onArchive={m.archiveDraft}
            onDuplicate={m.duplicateDraft} onPromote={m.promoteDraft} module="offer" />
        </>
      }
    />
  );
}
